var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as d3 from "d3";
import PowMon from "../../logic/powMon";
import { ColorScale, ColorStyling } from "../../styling/colorStyling";
import FontAwesomeStyling from "../../styling/fontAwesomeStyling";
import FormatUtil from "../../utils/formatUtil";
import MathUtil from "../../utils/mathUtil";
import PowMonLimitZoneUtil from "../../utils/powmonLimitZoneUtil";
export default class PrognosisBarChart {
    constructor(containerId, forWidget = false) {
        this._barMaxPad = .05;
        this._barColorActive = 'var(--active-power)';
        this._barColorReactive = 'var(--reactive-power)';
        this._barTitleActive = 'Active';
        this._barTitleReactive = 'Reactive';
        this._margin = { top: 40, right: 30, bottom: 10, left: 50 };
        this._width = 260 - this._margin.left - this._margin.right;
        this._height = 800 - this._margin.top - this._margin.bottom;
        d3.select(`#${containerId}`).selectAll('*').remove();
        this._forWidget = forWidget;
        if (forWidget) {
            this._margin = { top: 20, right: 15, bottom: 5, left: 25 };
            this._width = 100 - this._margin.left - this._margin.right;
            this._height = 300 - this._margin.top - this._margin.bottom;
        }
        this.initCharts(containerId);
    }
    initCharts(containerId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._limitsActive = PowMon.pLimits().reverse();
            this._limitsReactive = PowMon.qLimits().reverse();
            const charts = [
                {
                    htmlId: 'prognosis-chart-active',
                    title: this._barTitleActive,
                    yMax: this._limitsActive.length > 0 ? Math.max(...this._limitsActive.map(l => l.limit)) * (1 + this._barMaxPad) : 1,
                    yMin: 0,
                    prognosisValue: 0,
                    color: this._barColorActive,
                    limits: this._limitsActive
                },
                {
                    htmlId: 'prognosis-chart-reactive',
                    title: this._barTitleReactive,
                    yMax: MathUtil.safeMax(1, PowMon.levelQMax * (1 + this._barMaxPad * 2)),
                    yMin: MathUtil.safeMin(0, PowMon.levelQMin * (1 + this._barMaxPad * 2)),
                    prognosisValue: 0,
                    color: this._barColorReactive,
                    limits: this._limitsReactive
                }
            ];
            this.updateHeight(containerId);
            window.addEventListener('resize', () => {
                this.updateHeight(containerId);
                d3.selectAll("svg").attr("height", this._height + this._margin.top + this._margin.bottom);
                if (this._prognosis)
                    this.updateCharts(this._prognosis);
            });
            charts.forEach(chart => this.scaleChart(chart));
            const titleCell = d3.select(`#${containerId}`)
                .append("div")
                .attr("class", "powmon-title");
            titleCell.append("span").text(PowMon.noOfPrognosisMinutes === 60 ? "Hour prognosis" : "15 minute prognosis");
            titleCell.append("span").attr("class", "title-smaller-description").html(`<span class='before-text-icon'>for</span> ${FontAwesomeStyling.iconAndText("fa-sharp fa-regular fa-clock", "", "")}<span class="prognosis-time">--:--</span>`);
            d3.select(`#${containerId}`).append("div").attr("class", "charts-container");
            charts.forEach(chart => {
                const svg = d3.select(`.charts-container`)
                    .append("svg")
                    .attr("xmlns:xhtml", "http://www.w3.org/1999/xhtml")
                    .attr("id", chart.htmlId)
                    .attr("width", this._width + this._margin.left + this._margin.right)
                    .attr("height", this._height + this._margin.top + this._margin.bottom)
                    .append("g")
                    .attr("class", "chart")
                    .attr("transform", `translate(${this._margin.left},${this._margin.top})`);
                const x = d3.scaleBand()
                    .domain([chart.title])
                    .range([0, this._width])
                    .padding(.2);
                const y = d3.scaleLinear()
                    .domain([chart.yMin, chart.yMax])
                    .range([this._height, 0]);
                const yAxis = d3.axisLeft(y).ticks(6); //.tickFormat(d3.format(".2s"));
                svg.append("g")
                    .attr("class", "xAxis")
                    .attr("transform", `translate(0,${this._height})`)
                    .call(d3.axisTop(x).tickSize(0));
                d3.selectAll(".xAxis .tick text")
                    .attr("transform", `translate(0, -${this._height + 15})`);
                svg.append("g").attr("class", "yAxis").call(yAxis);
                chart.limits.forEach(limit => {
                    svg.append("line")
                        .attr("class", "limit-line")
                        .attr("x1", 0)
                        .attr("x2", this._width)
                        .attr("y1", y(limit.limit))
                        .attr("y2", y(limit.limit))
                        .attr("stroke", ColorStyling.alarmColor(limit.alarmLevel, ColorScale.Loud).toString())
                        .attr("stroke-width", 2.5);
                    if (!this._forWidget)
                        svg.append("text")
                            .attr("class", "limit-text")
                            .attr("x", 3)
                            .attr("y", y(limit.limit) - 8)
                            .attr("stroke-width", .5)
                            .attr("stroke", ColorStyling.alarmColor(limit.alarmLevel, ColorScale.Loud).toString())
                            .text(limit.name);
                });
                PowMonLimitZoneUtil.init(chart.limits, y, svg, chart.yMax, this._height, this._width, true);
                if (chart.htmlId === "prognosis-chart-reactive")
                    svg.append("line")
                        .attr("class", "zero-line")
                        .attr("x1", 0).attr("x2", this._width)
                        .attr("y1", y(0)).attr("y2", y(0))
                        .attr("stroke", "grey")
                        .attr("stroke-width", .5);
                svg.append("rect")
                    .attr("class", "bar")
                    .attr("x", x(chart.title))
                    .attr("width", x.bandwidth())
                    .attr("fill", chart.color);
            });
        });
    }
    updateCharts(prognosis) {
        return __awaiter(this, void 0, void 0, function* () {
            this._prognosis = prognosis;
            d3.select(".prognosis-time").text(FormatUtil.formatTime(prognosis.prognosisForTime, false, false));
            const pVal = prognosis.pVal ? prognosis.pVal : 0;
            const qVal = prognosis.qVal ? prognosis.qVal : 0;
            const charts = [
                {
                    htmlId: 'prognosis-chart-active',
                    yMax: MathUtil.safeMax(Math.max(...this._limitsActive.map(l => l.limit)), pVal) * (1 + this._barMaxPad),
                    yMin: 0,
                    title: `${this._barTitleActive} (${PowMon.scaledUnitP()})`,
                    prognosisValue: prognosis.pVal,
                    alarmLevel: PowMon.alarmLevelP(prognosis.pVal),
                    label: PowMon.formatP(prognosis.pVal),
                    limits: this._limitsActive
                },
                {
                    htmlId: 'prognosis-chart-reactive',
                    yMax: MathUtil.safeMax(PowMon.levelQMax, qVal) * (1 + this._barMaxPad * 2),
                    yMin: MathUtil.safeMin(0, MathUtil.safeMin(PowMon.levelQMin, qVal) * (1 + this._barMaxPad * 2)),
                    title: `${this._barTitleReactive} (${PowMon.scaledUnitQ()})`,
                    prognosisValue: prognosis.qVal,
                    alarmLevel: PowMon.alarmLevelQ(prognosis.qVal),
                    label: PowMon.formatQ(prognosis.qVal),
                    limits: this._limitsReactive
                }
            ];
            charts.forEach(chart => this.scaleChart(chart));
            charts.forEach(chart => {
                const y = d3.scaleLinear()
                    .domain([chart.yMin, chart.yMax])
                    .range([this._height, 0]);
                const yAxis = d3.axisLeft(y).ticks(6);
                d3.select(`#${chart.htmlId} .yAxis`)
                    .call(yAxis);
                d3.selectAll(`#${chart.htmlId} .limit-line`)
                    .data(chart.limits)
                    .attr("y1", d => y(d.limit))
                    .attr("y2", d => y(d.limit));
                d3.selectAll(`#${chart.htmlId} .limit-text`)
                    .data(chart.limits)
                    .attr("y", d => y(d.limit) - 8);
                PowMonLimitZoneUtil.update(chart.limits, d3.selectAll(`#${chart.htmlId} .limit-zone`), chart.yMax, y, this._height, true);
                d3.selectAll(`#${chart.htmlId} .zero-line`)
                    .attr("y1", y(0)).attr("y2", y(0));
                const bar = d3.selectAll(`#${chart.htmlId} .bar`)
                    .data([chart])
                    .attr("y", d => y(Math.max(0, d.prognosisValue)))
                    .attr("height", d => Math.abs(y(0) - y(d.prognosisValue)));
                d3.select(`#${chart.htmlId} .xAxis .tick text`).text(chart.title);
                const svg = d3.select(`#${chart.htmlId} .chart`);
                svg.selectAll("foreignObject").remove();
                const htmlOverlay = svg.append("foreignObject")
                    .attr("width", "100%").attr("height", "100%").attr("x", 0).attr("y", 0);
                const label = htmlOverlay.append("xhtml:div").attr("class", "prognosis-bar-label").style("position", "absolute").style("text-align", "center").style("width", `calc(100% - ${this._forWidget ? 30 : 76}px)`); //magic number
                const labelY = chart.prognosisValue ? y(chart.prognosisValue) : y(0);
                if (chart.prognosisValue < 0)
                    label.style("top", `${labelY + 5}px`);
                else
                    label.style("bottom", `${this._height - labelY + 55}px`);
                label.html(chart.prognosisValue ? FontAwesomeStyling.alarmIconAndText(chart.alarmLevel, chart.label) : "<span class='disabled'>Waiting for prognosis...</span>");
            });
        });
    }
    scaleChart(chart) {
        chart.yMax = PowMon.scale(chart.yMax);
        chart.yMin = PowMon.scale(chart.yMin);
        chart.prognosisValue = PowMon.scale(chart.prognosisValue);
        chart.limits = chart.limits
            .filter(limit => !isNaN(limit.limit))
            .map(limit => { return { name: limit.name, alarmLevel: limit.alarmLevel, limit: PowMon.scale(limit.limit) }; });
    }
    updateHeight(containerId, subtract = 100) {
        const container = document.getElementById(containerId);
        if (container) {
            const containerHeight = container.offsetHeight;
            this._height = containerHeight - subtract - this._margin.top - this._margin.bottom;
        }
    }
}
